import { Injectable, inject } from '@angular/core';
import { Auth } from '@freelancer/auth';
import type { Path, StoreState, TypedAction } from '@freelancer/datastore/core';
import { pluckDocumentFromRawStoreCollectionState } from '@freelancer/datastore/core';
import { isDefined } from '@freelancer/utils';
import { Actions, createEffect } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { filter, map, withLatestFrom } from 'rxjs/operators';
import type { VerificationProcessTypesCollection } from '../verification-process-types';

@Injectable()
// This allows the action to dispatch another one with a populated verification process type
// when the verification process type `API_PUSH_SUCCESS` is dispatched.
export class UserVerificationProcessesEffect {
  private actions$ = inject<Actions<TypedAction>>(Actions);
  private store$ = inject<Store<StoreState>>(Store);
  private auth = inject(Auth);

  readonly effect$ = createEffect(() =>
    this.actions$.pipe(
      filter(
        action =>
          action.type === 'API_PUSH_SUCCESS' &&
          action.payload.type === 'userVerificationProcesses' &&
          // Ensure that we are filtering only initial dispatch to avoid an infinite loop
          !isDefined(action.payload.result.verification_process_type),
      ),
      withLatestFrom(this.store$, this.auth.getUserId()),
      map(([action, state, userId]) => {
        const verificationProcessTypesPath: Path<VerificationProcessTypesCollection> =
          {
            collection: 'verificationProcessTypes',
            authUid: userId,
          };

        const verificationProcessType =
          pluckDocumentFromRawStoreCollectionState(
            state.verificationProcessTypes,
            verificationProcessTypesPath,
            action.payload.document.verificationProcessTypeId,
          );

        if (!verificationProcessType) {
          return undefined;
        }

        return {
          ...action,
          payload: {
            ...action.payload,
            result: {
              verification_process_type: verificationProcessType,
            },
          },
        };
      }),
      filter(isDefined),
    ),
  );
}
